import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, MessagesSquare } from 'lucide-react';
import type {
  AudienceIncludes,
  ConversationDetail,
  ConversationTransport,
} from '../api/conversations';
import { ApiError } from '../api/client';
import { createEventTopic } from '../api/eventTopics';
import type { EventTopic } from '../api/eventTopics';
import { subgroupsApi } from '../api/subgroups';
import type { Subgroup } from '../api/subgroups';
import { useConversation } from '../hooks/useConversation';
import { messageRuns } from '../lib/messageRuns';
import { Composer } from './Composer';
import { Gutter } from '../ui/Chrome';
import { ComplianceNotApplicable, ComplianceSatisfied } from '../ui/ComplianceNotice';
import { EmptyState } from '../ui/EmptyState';
import { FactRow } from '../ui/FactRow';
import { MessageBubble } from '../ui/MessageBubble';
import { UnreadDot } from '../ui/UnreadDot';
import { Button } from '../ui/Button';
import { SelectField, TextField } from '../ui/Field';
import { Overlay } from '../ui/Overlay';
import { RosterPicker } from '../ui/RosterPicker';

/**
 * The talk, below the 3px rule and on the sunken plane — §5A's third band.
 *
 * **One component, both doors** — TROOP-116. The member's page had this inline and the parent's had
 * a button reading "Open the thread", so a guardian could read an event's facts but not the
 * conversation about it without a second navigation. Conversations are one of the things a family
 * comes here for; `familyTransport` and `Composer` already existed, so the asymmetry was an
 * oversight rather than a decision.
 *
 * **The heading is a spine row, and that is the whole of TROOP-177.** Everything above the rule --
 * Depart, Where, Program, Going, Bring -- is a 100px label column, an icon, the answer in the value
 * column and an affordance at the right edge. This band opened with none of that: a run-on
 * `DISCUSSION · 3 Open the thread` starting at the gutter rather than in the label column, no icon,
 * and the count welded into the label. So the one section that is always expanded was also the one
 * section speaking a different language, and the page read as two pages stapled together.
 *
 * It is the same {@link FactRow} the record uses, so the column of labels now runs unbroken from
 * Depart to Thread and the answer -- `3 messages` -- sits where every other answer sits. The plane
 * change and the 3px rule still carry §5A's record/talk split; what they no longer carry is a
 * change of vocabulary, which was never their job.
 *
 * **The label is "Thread" and §5A says "Discussion", and that is a deliberate deviation.** The
 * 100px label column is the harder half of the same spec, and `DISCUSSION` does not fit it: at
 * 12px uppercase it paints 8px past the column and leaves 4px of air before the value where every
 * other row has 21px, so the one row meant to disappear into the spine was the one visibly
 * cramped. "Thread" is what the product already calls this object out loud -- the composer says
 * *Message the thread*, the empty conversations list says *the thread on every event* -- so the
 * label, the link beside it and the placeholder below it are now one word rather than three.
 * `FamilyThreadScreen` keeps "Discussion" as its page heading, where there is no column to hold.
 *
 * **Still not a `DocketRow`, deliberately.** Its label is ink, the way an *open* docket row's is,
 * because the talk beneath it is always shown -- collapsing it would end the page on a hard rule
 * above an empty coloured strip, and it would put the one thing a family comes here for behind a
 * chevron. The right-hand affordance is therefore a link to the full thread rather than a toggle:
 * the chevron lands in the same column as the docket's, and means what `LinkRow`'s means.
 *
 * **Bounded.** A thread renders its last {@link DRAWN} messages and sends the rest to the full
 * screen. The docket above exists to keep the page short, and two hundred bubbles underneath it
 * would undo that — the same trade `ParticipantStrip` and `/family`'s lists make, with the true
 * count kept in the heading.
 */
const DRAWN = 8;

export function EventThread({
  transport,
  conversationId,
  detail,
  meId,
  topics,
  unitId,
  eventId,
  canCreateTopic,
  subgroupLabelSingular,
  subgroupLabelPlural,
  onTopicsChanged,
  onOpenThread,
}: {
  /** `unitTransport(unitId)` on the member's door, `familyTransport` on the parent's. */
  transport: ConversationTransport;
  conversationId: string | null;
  /** For the compliance line and each author's classification. Null while it loads. */
  detail: ConversationDetail | null;
  meId: string;
  /**
   * The event's other conversations, if it has any this reader may see (TROOP-179).
   *
   * **Only ever the readable ones**, and the count in the row above is taken from this array rather
   * than from anything the server totalled separately. A number that included topics this person
   * cannot open would tell them an adult leaders' thread exists on their campout, which is the one
   * thing the feature is built to prevent.
   */
  topics?: EventTopic[];
  /**
   * Which unit's route to post a new topic to. Undefined on the guardian's door — a parent holds
   * no membership, so `/units/` 404s them.
   */
  unitId?: string;
  eventId?: string;
  /** The server's answer for this caller, never a constant. False for a guardian, always. */
  canCreateTopic?: boolean;
  /**
   * The unit's own word for a group — Patrol, Den, Crew (rule 16). Rendered into the topic form's
   * audience choice, never assumed: a literal here is what would make a Pack need a second screen.
   * Absent on the guardian's door, which offers no form.
   */
  subgroupLabelSingular?: string;
  subgroupLabelPlural?: string;
  /** Re-reads the topic list after one is opened. */
  onTopicsChanged?: () => void;
  onOpenThread: (conversationId: string) => void;
}) {
  const { messages, error, send, markRead } = useConversation(transport, conversationId ?? undefined);

  // Marks read only while the thread is genuinely in front of somebody. The visibility guard is
  // the point: without it a background reconcile would clear the badge with the phone in a pocket.
  useEffect(() => {
    if (messages.length === 0) {
      return;
    }

    const mark = () => {
      if (document.visibilityState === 'visible') {
        void markRead();
      }
    };

    mark();
    document.addEventListener('visibilitychange', mark);
    return () => document.removeEventListener('visibilitychange', mark);
  }, [messages, markRead]);

  const drawn = messages.slice(-DRAWN);
  const older = messages.length - drawn.length;

  // Who gets a name and who gets a timestamp. Computed over what is actually drawn, so the first
  // visible bubble introduces its author rather than inheriting a run from a message above the
  // fold. No unread divider on this screen, so nothing interrupts a run but a change of author.
  const runs = useMemo(() => messageRuns(drawn), [drawn]);

  // The topics beside the general discussion. The general one is already drawn above as this
  // band's own bubbles, so listing it again would be the same thread twice on one page.
  const besides = useMemo(() => (topics ?? []).filter((t) => !t.isGeneral), [topics]);

  const [opening, setOpening] = useState(false);
  const canOpen = Boolean(canCreateTopic && unitId && eventId);

  return (
    <Gutter>
      <FactRow
        label={
          // The real heading, wearing the row's type. A section this size owes the document an
          // <h2>; carrying it here rather than above the row is what keeps the label column
          // unbroken -- a heading of its own would have restarted the band at the gutter, which
          // is the thing being fixed.
          <h2 style={{ font: 'inherit', letterSpacing: 'inherit', margin: 0 }}>Thread</h2>
        }
        icon={MessagesSquare}
        labelTone="ink"
        // Under the count, where a street address goes under a place name. It is a qualification
        // of the talk rather than a fact of its own, and §5A wants it in the heading -- but the
        // right column is one affordance wide, and a chip sharing it with the link would put two
        // things where every row above has one.
        secondary={
          detail?.complianceState === 'Satisfied' ? (
            <ComplianceSatisfied adultCount={detail.adultCount} />
          ) : detail?.complianceState === 'NotApplicable' ? (
            <ComplianceNotApplicable />
          ) : undefined
        }
        trailing={
          conversationId ? (
            <button
              type="button"
              className="link-button t-caption inline-flex items-center gap-1"
              style={{ whiteSpace: 'nowrap' }}
              onClick={() => onOpenThread(conversationId)}
            >
              Open the thread
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          ) : undefined
        }
      >
        {/* The answer, not a category -- `DocketRow`'s own rule, and the reason the count moved
            out of the label. "Nothing yet" rather than the empty state's own words, so the row
            and the box beneath it are not the same sentence twice.

            The topic count is derived from the rows this reader actually received, never from a
            separate total: a "2 topics" that included one they cannot open would disclose that an
            adults-only thread exists on their event (TROOP-179). */}
        {rowValue(messages.length, besides.length)}
      </FactRow>

      {/* The panel, spaced as a docket panel is: content under the row's rule, and the band's own
          breathing room comes from the section below the 3px rule rather than from here. */}
      <div style={{ paddingTop: 'var(--space-4)' }}>
        {messages.length === 0 ? (
          <EmptyState
            icon={MessagesSquare}
            headline="Nothing asked yet"
            explanation="The facts above are the answer to most of it. Anything else goes here."
          />
        ) : (
          <div>
            {older > 0 && (
              <p style={{ margin: '0 0 var(--space-3)' }}>
                <button
                  type="button"
                  className="link-button t-caption"
                  onClick={() => conversationId && onOpenThread(conversationId)}
                >
                  Read the {older} earlier {older === 1 ? 'message' : 'messages'}
                </button>
              </p>
            )}

            {drawn.map((message, i) => (
              <MessageBubble
                key={message.id}
                message={{
                  id: message.id,
                  authorName: message.authorDisplayName,
                  authorClassification:
                    detail?.participants.find((p) => p.personId === message.authorPersonId)
                      ?.classification === 'Youth'
                      ? 'youth'
                      : 'adult',
                  body: message.isRemoved ? '' : (message.body ?? ''),
                  time: runs[i].time,
                  own: message.authorPersonId === meId,
                  startsRun: runs[i].startsRun,
                  removed: message.isRemoved,
                  removalBasis: message.removalBasis,
                }}
                // No action sheet here, and that is the one place the three thread renderings
                // deliberately differ (TROOP-12). This is the tail of a thread inside an event
                // docket -- it already sends the reader to the full thread for everything else,
                // and a per-message control on a preview would put a modal on top of a page that
                // is mostly facts. The tombstone still renders, because that is what the thread
                // owes every reader (section 5).
              />
            ))}
          </div>
        )}

        {error && (
          <p
            className="t-caption"
            role="alert"
            style={{ color: 'var(--color-status-blocked)', marginTop: 'var(--space-3)' }}
          >
            {error}
          </p>
        )}

        <div style={{ marginTop: 'var(--space-5)' }}>
          {/* Sends in place. The fixture version of this screen navigated away when the box
              took focus, because it had no working send; that would now make the composer
              impossible to type into. Opening the full thread is the explicit link above. */}
          <Composer onSend={send} />
        </div>

        {/* The other topics, under the general discussion rather than beside it -- TROOP-179.
            Somebody with a question and no idea where it goes meets the composer first, which is
            what keeps the general discussion the default rather than the thing nobody uses once
            everything has been filed. Rows, never expanded threads: five open conversations
            stacked down the page is exactly the shape TROOP-116 took off this screen. */}
        {(besides.length > 0 || canOpen) && (
          <section style={{ marginTop: 'var(--space-6)' }}>
            <h3
              className="t-label"
              style={{ color: 'var(--color-text-muted)', margin: '0 0 var(--space-2)' }}
            >
              Topics
            </h3>

            {besides.length === 0 ? (
              <p
                className="t-caption"
                style={{ color: 'var(--color-text-secondary)', margin: '0 0 var(--space-3)' }}
              >
                Everything about this event is in the one thread above. A topic gives a smaller
                group their own.
              </p>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {besides.map((topic) => (
                  <li key={topic.conversationId}>
                    <button
                      type="button"
                      onClick={() => onOpenThread(topic.conversationId)}
                      className="flex items-center w-full text-left"
                      style={{
                        gap: 'var(--space-3)',
                        minHeight: 'var(--tap-min)',
                        padding: 'var(--space-3) 0',
                        background: 'none',
                        // `border: none` then a borderTop would be two declarations racing; the
                        // longhand alone says the one true thing.
                        borderWidth: '1px 0 0 0',
                        borderStyle: 'solid',
                        borderColor: 'var(--color-border-subtle)',
                        cursor: 'pointer',
                      }}
                    >
                      <span className="flex-1 min-w-0">
                        <span
                          className="t-body-strong block"
                          style={{ color: 'var(--color-text-primary)' }}
                        >
                          {topic.title}
                        </span>
                        <span
                          className="t-caption block"
                          style={{ color: 'var(--color-text-secondary)' }}
                        >
                          {audienceWords(topic)}
                          {' · '}
                          <span className="tabular">{topic.messageCount}</span>
                          {topic.messageCount === 1 ? ' message' : ' messages'}
                        </span>
                      </span>

                      {topic.unreadCount > 0 && <UnreadDot count={topic.unreadCount} />}

                      <ChevronRight
                        size={16}
                        aria-hidden="true"
                        style={{ color: 'var(--color-text-muted)' }}
                      />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {canOpen && (
              <p style={{ margin: 'var(--space-3) 0 0' }}>
                <button
                  type="button"
                  className="link-button t-caption"
                  onClick={() => setOpening(true)}
                >
                  Open a topic
                </button>
              </p>
            )}
          </section>
        )}

        {opening && canOpen && (
          <OpenTopic
            unitId={unitId!}
            eventId={eventId!}
            subgroupLabelSingular={subgroupLabelSingular ?? 'Group'}
            subgroupLabelPlural={subgroupLabelPlural ?? 'Groups'}
            onClose={() => setOpening(false)}
            onOpened={() => {
              setOpening(false);
              onTopicsChanged?.();
            }}
          />
        )}
      </div>
    </Gutter>
  );
}

/**
 * Opening a topic — TROOP-179.
 *
 * **Four audiences, named for what they are rather than for the enum.** The choice is the whole
 * feature: a parents' topic and an adult leaders' one differ in nothing else, and a leader picking
 * between them is picking who can see the thread at all. So each option carries the sentence that
 * says who is actually in it — including the one most likely to be got wrong, which is that
 * "Parents and leaders" derives the guardians of youth members and "Adult leaders" derives none.
 *
 * The slug is derived from the name rather than asked for. A leader typing "Parents" should not
 * also have to understand what a key is, and the server is what enforces uniqueness anyway.
 */
function OpenTopic({
  unitId,
  eventId,
  subgroupLabelSingular,
  subgroupLabelPlural,
  onClose,
  onOpened,
}: {
  unitId: string;
  eventId: string;
  /** The unit's own word for a group -- Patrol, Den, Crew. Rendered, never assumed (rule 16). */
  subgroupLabelSingular: string;
  subgroupLabelPlural: string;
  onClose: () => void;
  onOpened: () => void;
}) {
  const [title, setTitle] = useState('');
  const [choice, setChoice] = useState<AudienceChoice>('AdultsOnly');
  const [subgroupId, setSubgroupId] = useState('');
  const [subgroups, setSubgroups] = useState<Subgroup[] | null>(null);
  const [named, setNamed] = useState<{ personId: string; displayName: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Fetched once, when the form opens, rather than on picking the option. Loading a list the
  // moment somebody chooses puts an empty picker in front of them for as long as the request
  // takes, which reads as a broken control rather than a slow one.
  useEffect(() => {
    let live = true;

    subgroupsApi
      .list(unitId)
      .then((rows) => live && setSubgroups(rows.filter((s) => !s.isRetired)))
      .catch(() => live && setSubgroups([]));

    return () => {
      live = false;
    };
  }, [unitId]);

  const explicit = choice === 'Named';
  const byGroup = choice === 'Group';

  // Only what the picker must not offer again. The creator is added server-side, so leaving them
  // out here would be a second copy of that rule -- and one that says nothing if it drifts.
  const alreadyNamed = useMemo(() => new Set(named.map((p) => p.personId)), [named]);

  const submit = async () => {
    setError(null);
    setSaving(true);

    try {
      await createEventTopic(unitId, eventId, {
        // Trimmed here, at submit, and never in the onChange -- these boxes are controlled, so
        // trimming in the handler eats the trailing space and the space bar appears dead.
        key: slugOf(title),
        title: title.trim(),
        audienceMode: explicit ? 'Explicit' : 'RosterScoped',
        // A group's topic is the whole-unit audience narrowed to that group: its members and their
        // guardians, with the unit's adult officers still derived in as monitors. That last part is
        // what makes a patrol's thread compliant with no adult in the patrol.
        audienceIncludes: explicit || byGroup ? 'Everyone' : choice,
        participantIds: explicit ? named.map((p) => p.personId) : undefined,
        audienceSubgroupId: byGroup ? subgroupId : undefined,
      });

      onOpened();
    } catch (e) {
      // The server's sentence: a Pack that holds no young members, a name already taken, the
      // two-adult quorum on a named list that has one adult in it. Each names something a leader
      // can act on, and the form deliberately pre-judges none of them -- a copy of those rules
      // here would drift from the originals the day one changes.
      setError(e instanceof ApiError ? e.message : 'Could not open the topic.');
      setSaving(false);
    }
  };

  const slug = slugOf(title);

  const blocked =
    title.trim().length === 0 || slug.length === 0
      ? 'Give the topic a name first.'
      : slug === 'general'
        ? 'Every event already has its general discussion. Pick another name.'
        : byGroup && !subgroupId
          ? `Pick which ${subgroupLabelSingular.toLowerCase()} it is for.`
          : explicit && named.length === 0
            ? 'Add at least one person.'
            : null;

  return (
    <Overlay title="Open a topic" onClose={onClose}>
      <div className="flex flex-col" style={{ gap: 'var(--space-4)' }}>
        <p className="t-body" style={{ color: 'var(--color-text-secondary)', margin: 0, maxWidth: '46ch' }}>
          A second conversation on this event, for a smaller group. Everything else stays in the
          thread everybody reads.
        </p>

        {error && (
          <p
            className="t-body"
            role="alert"
            style={{
              color: 'var(--color-text-primary)',
              background: 'var(--color-status-blocked-surface)',
              border: '1px solid var(--color-status-blocked)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-3) var(--space-4)',
              margin: 0,
            }}
          >
            {error}
          </p>
        )}

        <TextField
          label="Name"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Food plan"
          required
          autoComplete="off"
        />

        {/* The unit's own word in the option, never "patrol" (rule 16) -- it is what makes a Pack a
            configuration change rather than a second set of screens. */}
        <SelectField
          label="Who can see it"
          value={choice}
          required
          hint="This is the whole of it. Nobody outside the group you pick can see the topic, or
                that it exists."
          onChange={(e) => setChoice(e.target.value as AudienceChoice)}
        >
          <option value="AdultsOnly">Parents and leaders</option>
          <option value="OfficersOnly">Adult leaders only</option>
          <option value="YouthAndLeaders">Young members, with leaders</option>
          <option value="Everyone">Everyone in the unit</option>
          <option value="Group">One {subgroupLabelSingular.toLowerCase()}</option>
          <option value="Named">Named people</option>
        </SelectField>

        {byGroup && (
          // A picker, not a list. The dev troop carries 145 live groups and a real unit that grows
          // past a dozen reads the same way -- a row per group is the bug TROOP-74 already found
          // once on this very page.
          <SelectField
            label={subgroupLabelSingular}
            value={subgroupId}
            required
            onChange={(e) => setSubgroupId(e.target.value)}
          >
            <option value="">
              {subgroups === null ? 'Loading...' : `Pick a ${subgroupLabelSingular.toLowerCase()}`}
            </option>
            {(subgroups ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </SelectField>
        )}

        {byGroup && subgroups !== null && subgroups.length === 0 && (
          <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
            This unit has no {subgroupLabelPlural.toLowerCase()} yet. Manage adds them.
          </p>
        )}

        {explicit && (
          <div className="flex flex-col" style={{ gap: 'var(--space-3)' }}>
            {/* Nothing derives into a named list, so the two-adult quorum has to be satisfied
                deliberately -- one youth and one adult is refused, and the sentence saying so comes
                from the server. Saying it here as well would be a second copy of I1. */}
            <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0, maxWidth: '46ch' }}>
              Only these people, and you. A topic with a young person in it needs two adults, the
              same as any other thread.
            </p>

            {named.length > 0 && (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {named.map((p) => (
                  <li
                    key={p.personId}
                    className="flex items-center gap-3"
                    style={{ minHeight: 'var(--tap-min)' }}
                  >
                    <span className="t-body flex-1" style={{ color: 'var(--color-text-primary)' }}>
                      {p.displayName}
                    </span>
                    <button
                      type="button"
                      className="link-button t-caption"
                      aria-label={`Take ${p.displayName} off the list`}
                      onClick={() =>
                        setNamed((was) => was.filter((x) => x.personId !== p.personId))
                      }
                      style={{ minHeight: 'var(--tap-min)' }}
                    >
                      Take off
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <RosterPicker
              unitId={unitId}
              label="Who is in it"
              exclude={alreadyNamed}
              onPick={(row) =>
                setNamed((was) => [...was, { personId: row.personId, displayName: row.displayName }])
              }
            />
          </div>
        )}

        <div className="flex flex-wrap" style={{ gap: 'var(--space-3)' }}>
          <Button
            onClick={submit}
            {...(saving
              ? { disabledReason: 'Opening it now.' }
              : blocked
                ? { disabledReason: blocked }
                : {})}
          >
            Open the topic
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Overlay>
  );
}

/**
 * What the form offers, which is not the same list as `AudienceIncludes`.
 *
 * Two of the six are shapes rather than audiences: `Group` is the whole-unit audience narrowed to
 * one group, and `Named` is an explicit list that derives nobody. Keeping them in one control is
 * what makes it one question -- *who can see it* -- rather than three, and the mapping back to the
 * wire happens once, at submit.
 */
type AudienceChoice = AudienceIncludes | 'Group' | 'Named';

/** "Food plan" becomes "food-plan". Letters and hyphens only, which is what the server accepts. */
const slugOf = (title: string) =>
  title
    .trim()
    .toLowerCase()
    .replace(/[^a-z]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);

/**
 * What the row above says: the general discussion's size, and how many other topics this reader
 * can see. Never a total the server computed over topics they cannot open.
 */
function rowValue(messages: number, topics: number): string {
  const said = messages === 0 ? 'Nothing yet' : `${messages} ${messages === 1 ? 'message' : 'messages'}`;

  if (topics === 0) return said;

  return `${said} · ${topics} ${topics === 1 ? 'topic' : 'topics'}`;
}

/**
 * Who a topic is for, in the unit's own register rather than the enum's.
 *
 * `AdultsOnly` genuinely means adult members *and* the guardians of youth members -- it is the
 * parents-and-leaders room and the parents are the point of it -- so calling it "Adults" on screen
 * would read as excluding them. `OfficersOnly` is memberships only and derives no guardians, which
 * is the distinction most likely to be got wrong by whoever writes the next copy here.
 */
function audienceWords(topic: EventTopic): string {
  if (topic.audienceMode === 'Explicit') return 'Named people';

  // The group's own name wins over the audience it narrows, because "Otter" is what the reader
  // came for and "Everyone" would be actively wrong about a patrol's thread. No label in front of
  // it: the unit's word for what a group IS lives on the session payload, and putting "Patrol
  // Otter" together here would be the second place that word is composed (rule 16).
  if (topic.audienceSubgroupName) return topic.audienceSubgroupName;

  switch (topic.audienceIncludes) {
    case 'AdultsOnly':
      return 'Parents and leaders';
    case 'OfficersOnly':
      return 'Adult leaders';
    case 'YouthAndLeaders':
      return 'Young members, with leaders';
    default:
      return 'Everyone';
  }
}
